import type {
  CurrencyBlock,
  PreviewPositionGroup,
  PreviewSection,
  SecurityTotal,
  StatementDocument,
} from '@/types';

interface ItemBase {
  /** Stable across refetches of the same document: the virtualizer keys its
   * measurements by it, and a group's opened state is kept under it. */
  key: string;
  block: CurrencyBlock;
  blockIndex: number;
  /** Index of this item's block heading in the list. */
  blockHead: number;
}

interface InSection extends ItemBase {
  section: PreviewSection;
  /** Index of this item's section heading in the list. */
  sectionHead: number;
  /** First of its kind inside its parent - carries no top spacing. */
  first: boolean;
}

export type StatementItem =
  | (ItemBase & { kind: 'block-head' })
  | (ItemBase & { kind: 'block-total' })
  | (InSection & { kind: 'section-head' })
  | (InSection & {
      kind: 'group';
      group: PreviewPositionGroup;
      /** The security's total, on the group that closes its run of trades. */
      securityTotal: SecurityTotal | undefined;
    });

export const blockKey = (block: CurrencyBlock) =>
  `${block.currency}-${block.agreement}-${block.account}`;

/**
 * The trade document as one list, in reading order: block heading, then per
 * section its heading and its position groups, then the block total. The same
 * order `document_to_matrix` writes the CSV/PDF in, so the screen and the file
 * enumerate one document. A group is one item with its daily lines: an opened
 * ledger stays one HTML table rather than rows scattered across the list.
 */
export const flattenStatement = (
  document: StatementDocument
): StatementItem[] => {
  const items: StatementItem[] = [];
  document.blocks.forEach((block, blockIndex) => {
    const bk = blockKey(block);
    const blockHead = items.length;
    items.push({
      kind: 'block-head',
      key: `b:${bk}`,
      block,
      blockIndex,
      blockHead,
    });
    block.sections.forEach((section, si) => {
      const sectionHead = items.length;
      const base = { block, blockIndex, blockHead, section, sectionHead };
      items.push({
        ...base,
        kind: 'section-head',
        key: `s:${bk}:${section.key}`,
        first: si === 0,
      });
      if (section.is_empty) return;
      // A security lent on several trades is footed once beneath them (LR-632).
      // The builder lists only securities with more than one trade and orders
      // groups by symbol, so the run closes when the next group changes security.
      const totals = new Map(
        (section.securities ?? []).map((sec) => [sec.symbol_id, sec] as const)
      );
      section.groups.forEach((group, gi) => {
        const closesRun = section.groups[gi + 1]?.symbol_id !== group.symbol_id;
        items.push({
          ...base,
          kind: 'group',
          key: `g:${bk}:${section.key}:${group.position_id}`,
          first: gi === 0,
          group,
          securityTotal: closesRun ? totals.get(group.symbol_id) : undefined,
        });
      });
    });
    items.push({
      kind: 'block-total',
      key: `t:${bk}`,
      block,
      blockIndex,
      blockHead,
    });
  });
  return items;
};

/**
 * Which block and section headings have scrolled above the item at `index` -
 * the ones a sticky header has to repeat so a line far down a section still
 * says which currency and section it belongs to.
 */
export const stickyContext = (
  items: StatementItem[],
  index: number
): { block: CurrencyBlock | null; section: PreviewSection | null } => {
  const item = items[index];
  if (item == null) return { block: null, section: null };
  return {
    block: index > item.blockHead ? item.block : null,
    section:
      'sectionHead' in item && index > item.sectionHead ? item.section : null,
  };
};

/**
 * The groups the document's search finds, as list indices in reading order: each
 * is a `scrollToIndex` target. A group is found by its trade id as printed
 * (`TRD-123`), its ticker or its ISIN, in any case. The three are joined by a
 * line break, which a one-line field cannot type, so a query never matches
 * across two of them.
 */
export const findGroups = (items: StatementItem[], query: string): number[] => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const found: number[] = [];
  items.forEach((item, i) => {
    if (item.kind !== 'group') return;
    const { position_id, symbol_id, isin } = item.group;
    if (`TRD-${position_id}\n${symbol_id}\n${isin}`.toLowerCase().includes(q)) {
      found.push(i);
    }
  });
  return found;
};

/**
 * Where a search starts among what `findGroups` found: on the trade whose id the
 * query names whole (`TRD-12`, `12`), else on the first match. In reading order
 * TRD-1234 can come long before TRD-12, and an id is typed to find that trade.
 */
export const searchStart = (
  items: StatementItem[],
  found: number[],
  query: string
): number => {
  const id = /^(?:trd-)?(\d+)$/i.exec(query.trim())?.[1];
  if (id === undefined) return 0;
  const exact = found.findIndex((i) => {
    const item = items[i];
    return item.kind === 'group' && String(item.group.position_id) === id;
  });
  return Math.max(exact, 0);
};
